/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable, HttpsCallableOptions } from '@angular/fire/functions';
import { Observable, from } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { IGetSignedUrlRequest } from '@touchplan/common';

@Injectable({
	providedIn: 'root',
})
export class FirebaseCloudFunctionsService {
	private functionsInstance: Functions = inject(Functions);

	/**
	 * Checks the connectivity to the cloud.
	 *
	 * @returns An observable that emits an empty object.
	 */
	public checkCloudConnectivity(): Observable<null> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<undefined, null>(
			this.functionsInstance,
			'mb4-testConnectivity'
		);
	}

	/**
	 * Gets the download URL for a file with the specified GCS path and bucket name.
	 *
	 * @param gcsPath - The GCS path of the file.
	 * @param bucketName - The name of the bucket where the file is stored.
	 * @returns An observable that emits the download URL for the file.
	 */
	public getFileUrl$(gcsPath: string, bucketName: string): Observable<string> {
		//chop off opening slash
		if (gcsPath[0] === '/') {
			gcsPath = gcsPath.slice(1, gcsPath.length);
		}
		//temp thing because of that stupid double slash bug
		gcsPath = gcsPath
			.split('/')
			.filter((s) => s.length > 0)
			.join('/');

		return FirebaseCloudFunctionsService.callFunctionAsObservable$<IGetSignedUrlRequest, string>(
			this.functionsInstance,
			'mb4-downloadFileUrl',
			{
				gcsPath,
				bucketName,
			}
		);
	}

	/**
	 * Calls a Firebase Cloud Function asynchronously and returns the result as an observable.
	 * It also transforms the response to extract the 'data' property and implements a completion
	 * strategy to mitigate the risk of memory leaks.
	 *
	 * @param firebaseFunctionsInstance - The instance of Firebase Functions.
	 * @param functionName - The name of the Cloud Function to be called.
	 * @param requestData - The data to be sent as the request to the Cloud Function.
	 * @param firebaseCallableOptions - Firebase Callable Options to configure the behavior of the callable function.
	 * @returns An observable that emits the response data from the Cloud Function.
	 */
	static callFunctionAsObservable$<R, T>(
		firebaseFunctionsInstance: Functions,
		functionName: string,
		requestData?: R,
		firebaseCallableOptions?: HttpsCallableOptions
	) {
		return from(
			httpsCallable<R, T>(firebaseFunctionsInstance, functionName, firebaseCallableOptions)(requestData)
		).pipe(
			take(1),
			map((response) => response?.data || null)
		);
	}

	public checkCloudConnectivityAuth(): Observable<null> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<undefined, null>(
			this.functionsInstance,
			'mb4-testConnectivityAuthentication'
		);
	}
}
